// CursorClickIcon has no SpriteMap equivalent; kept until a sprite is added.

import { type ButtonProps, Tab } from '@headlessui/react'
import { CursorClickIcon } from '@heroicons/react/outline'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import type {
  Feature as GeoJSONFeature,
  FeatureCollection as GeoJSONFeatureCollection
} from 'geojson'
import { Fragment, type PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import {
  ButtonKind,
  CircleDrawIcon,
  EditBoundaryIcon,
  EraseIcon,
  FreehandIcon,
  Icon,
  OptionButton,
  PanoramaButton,
  PolygonIcon,
  RectangleIcon,
  SplitIcon,
  UndoIcon
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import { DrawMode, DrawStyle, useDrawMode, useDrawShape, useMapContext } from 'shared-ui/map'
import { isNil } from 'utils'

export const withZone = <T extends GeoJSONFeatureCollection | GeoJSONFeature>(
  geojson: T,
  zone?: string
): T => {
  if (isNil(zone)) {
    return geojson
  }

  if (geojson.type === 'FeatureCollection') {
    return {
      ...geojson,
      features: geojson.features.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          zone
        }
      }))
    }
  }

  return {
    ...geojson,
    properties: {
      ...geojson.properties,
      zone
    }
  }
}

export const AreaToken = ({
  reset,
  onReset,
  type,
  area,
  Icon
}: {
  reset?: boolean
  onReset?: () => void
  type: string
  area: number
  Icon: ({ className }: { className: string }) => ReactNode
}) => {
  const { t, formatMetric } = useLocalization()

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-gray-100 px-4 py-3">
      <div className="flex w-full items-center gap-2">
        <Icon className="size-4 text-gray-600" />
        <span className="text-sm text-gray-500">{type}</span>
        {reset === true && (
          <button
            type="button"
            onClick={onReset}
            className="ml-auto flex items-center gap-1 text-sm font-medium text-green-500"
          >
            {t(($) => $.buttons.reset)} <UndoIcon />
          </button>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold text-gray-900">
          {formatMetric({ value: area, unit: 'ac' })}
        </span>
      </div>
    </div>
  )
}

// Stable ID used to coordinate the base rate zone between AutoGenerateOptions (preview)
// and PrescriptionDrawing (editing). Both components stamp/create the same zone so that
// the boundary features loaded during preview remain colored correctly when drawing starts.
export const PRESCRIPTION_BASE_ZONE_ID = 'prescription-base-rate'

// Stable ID used to tag boundary features loaded into the draw tool for guidance editing.
// Must match wherever createZone and withZone stamp boundary features.
export const BOUNDARY_ZONE_ID = 'Boundary0'

const shapeOptions = [
  { type: DrawStyle.Freehand, Icon: FreehandIcon },
  { type: DrawStyle.Polygon, Icon: PolygonIcon },
  { type: DrawStyle.Circle, Icon: CircleDrawIcon },
  { type: DrawStyle.Rectangle, Icon: RectangleIcon }
] as const

const ModeSecondaryOption = {
  prescription: {
    mode: DrawMode.Split,
    Icon: SplitIcon,
    shape: DrawStyle.Line
  },
  guidance: {
    mode: DrawMode.Split,
    Icon: SplitIcon,
    shape: DrawStyle.Line
  },
  guidanceRegion: {
    mode: DrawMode.Split,
    Icon: SplitIcon,
    shape: DrawStyle.Line
  },
  boundary: { mode: DrawMode.Erase, Icon: EraseIcon, shape: undefined }
} as const

const drawModesWithDisabledShapes = [DrawMode.Split, DrawMode.AssignZone]

export const ToolsAndShapes = ({
  type
}: {
  type: 'prescription' | 'boundary' | 'guidance' | 'guidanceRegion'
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()

  const primaryMode = type === 'boundary' ? DrawMode.Draw : DrawMode.AddZone
  const modeState = useDrawMode()
  const drawShapeState = useDrawShape()

  useEffect(() => {
    map.drawTool.setDrawStyle({ mode: primaryMode, shape: DrawStyle.Polygon })
  }, [])

  useEffect(() => () => map.drawTool.disable(), [])

  const { Icon, mode: secondaryMode, shape: secondaryShape } = ModeSecondaryOption[type]

  return (
    <div className="flex w-full flex-col gap-2 py-4">
      <div className="flex items-center justify-between">
        <div className="pan-title4">
          {type === 'prescription' || type === 'guidanceRegion'
            ? t(($) => $.planning.boundaryCreation.createRegions)
            : t(($) => $.planning.boundaryCreation.toolsAndShapes)}
        </div>
        <PanoramaButton
          type="button"
          kind={ButtonKind.Secondary}
          className="h-fit py-1"
          onClick={map.drawTool.undo}
        >
          {t(($) => $.buttons.undo)}
          <UndoIcon />
        </PanoramaButton>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <OptionButton
          title={t(($) => $.buttons.add)}
          onClick={() =>
            map.drawTool.setDrawStyle({
              mode: primaryMode,
              shape: drawShapeState === secondaryShape ? DrawStyle.Polygon : drawShapeState
            })
          }
          description={
            type === 'guidanceRegion'
              ? t(($) => $.planning.boundaryCreation.addDescription.region)
              : t(($) => $.planning.boundaryCreation.addDescription[primaryMode])
          }
          active={modeState === primaryMode}
          IconElement={EditBoundaryIcon}
        />
        <OptionButton
          title={t(($) => $.buttons[secondaryMode])}
          onClick={() =>
            map.drawTool.setDrawStyle({
              mode: secondaryMode,
              shape: secondaryShape ?? drawShapeState
            })
          }
          description={
            type === 'guidanceRegion' && secondaryMode === DrawMode.Split
              ? t(($) => $.planning.boundaryCreation.splitRegionDescription)
              : t(($) => $.planning.boundaryCreation[`${secondaryMode}Description`])
          }
          active={modeState === secondaryMode}
          IconElement={Icon}
        />
        {(type === 'prescription' || type === 'guidanceRegion') && (
          <OptionButton
            title={t(($) => $.buttons.reassign)}
            onClick={() =>
              map.drawTool.setDrawStyle({
                mode: DrawMode.AssignZone,
                shape: drawShapeState
              })
            }
            description={
              type === 'guidanceRegion'
                ? t(($) => $.planning.boundaryCreation.reassignRegionDescription)
                : t(($) => $.planning.boundaryCreation.reassignDescription)
            }
            active={modeState === DrawMode.AssignZone}
            IconElement={CursorClickIcon}
          />
        )}
      </div>
      <div className="pan-title5">{t(($) => $.planning.boundaryCreation.selectShape)}</div>
      <div className="grid grid-cols-2 gap-2">
        {shapeOptions.map(({ type: shapeType, Icon }) => (
          <OptionButton
            key={shapeType}
            disabled={drawModesWithDisabledShapes.includes(modeState)}
            onClick={() => map.drawTool.setDrawStyle({ mode: modeState, shape: shapeType })}
            title={t(($) => $.planning.boundaryCreation.shapes[shapeType])}
            active={drawShapeState === shapeType}
            IconElement={Icon}
          />
        ))}
      </div>
    </div>
  )
}

export const GuidanceTools = ({ onChange }: { onChange: (type: GuidancePrimitive) => void }) => {
  const { map } = useMapContext()
  const { t } = useLocalization()

  const [modeState, setModeState] = useState<DrawMode>(DrawMode.ABLine)
  const [drawShapeState, setDrawShapeState] = useState<DrawStyle>(DrawStyle.Line)

  const lineOptions = [
    {
      guidanceType: GuidancePrimitive.ABLine,
      optionMode: DrawMode.ABLine,
      type: DrawStyle.Line,
      icon: SpriteMap.GuidanceStraightIcon
    },
    {
      guidanceType: GuidancePrimitive.AHeading,
      optionMode: DrawMode.AHeading,
      type: DrawStyle.Line,
      icon: SpriteMap.GuidanceHeadingIcon
    },
    {
      guidanceType: GuidancePrimitive.Curve,
      optionMode: DrawMode.Draw,
      type: DrawStyle.Line,
      icon: SpriteMap.GuidanceCurveIcon
    },
    {
      guidanceType: GuidancePrimitive.RadialPivot,
      optionMode: DrawMode.RadialPivot,
      type: DrawStyle.Line,
      icon: SpriteMap.GuidanceCircleIcon
    }
  ] as const

  return (
    <>
      <div className="flex w-full flex-col gap-2 py-4">
        <div className="pan-title4">{t(($) => $.planning.drawGuidanceLines)}</div>
        <div className="grid grid-cols-2 gap-2">
          {lineOptions.map(({ guidanceType, optionMode, type: shapeType, icon }) => (
            <OptionButton
              key={guidanceType}
              onClick={() => {
                onChange(guidanceType)
                setModeState(optionMode)
                setDrawShapeState(shapeType)
                map.drawTool.setDrawStyle({
                  mode: optionMode,
                  shape: shapeType
                })
              }}
              title={t(($) => $.planning.guidancePrimitiveLabels[guidanceType])}
              active={modeState === optionMode && drawShapeState === shapeType}
              IconElement={({ className }) => <Icon icon={icon} className={className} />}
            />
          ))}
        </div>
      </div>
      {/*<ToolsAndShapes type="guidance" />*/}
    </>
  )
}

export const EditButtons = ({
  back,
  edit
}: {
  back: (type: undefined) => void
  edit: () => void
}) => {
  const { t } = useLocalization()
  return (
    <div className="flex h-fit w-full gap-3 p-5">
      <PanoramaButton
        type="button"
        kind={ButtonKind.Secondary}
        onClick={() => back(undefined)}
        className="w-full"
      >
        {t(($) => $.buttons.back)}
      </PanoramaButton>

      <PanoramaButton onClick={edit} className="w-full border-gray-300">
        {t(($) => $.planning.editCopy)}
        <EditBoundaryIcon className="" />
      </PanoramaButton>
    </div>
  )
}

const tabButtonClasses = createVariants({
  base: 'pan-title5 w-full py-2.5 text-gray-500 transition-colors rounded',
  variants: {
    selected: {
      true: 'bg-accent-100 text-green-700 font-bold'
    },
    disabled: {
      true: 'opacity-40 cursor-not-allowed',
      false: 'hover:bg-gray-50 hover:text-gray-800'
    }
  }
})

export const StyledTab: React.FC<PropsWithChildren & ButtonProps> = ({ children, ...rest }) => {
  return (
    <Tab {...rest} as={Fragment}>
      {({ selected, disabled }) => (
        <button
          type="button"
          disabled={disabled}
          className={tabButtonClasses({ selected, disabled })}
        >
          {children}
        </button>
      )}
    </Tab>
  )
}
